<template>
  <div>
    <div class="d-flex justify-content-end align-items-center mb-3">
      <span class="badge" :class="statusClass(form.status)">
        {{ statusLabel(form.status) }}
      </span>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-5">
        <label class="form-label small fw-semibold">Commande fournisseur *</label>
        <select
          class="form-select form-select-sm"
          v-model="form.purchase_id"
          :disabled="mode === 'edit' || isLoadingPurchases || submitting"
          @change="loadPurchaseReturnables"
        >
          <option :value="null">Sélectionner une commande validée</option>
          <option v-for="purchase in purchases" :key="purchase.id" :value="purchase.id">
            {{ purchase.reference }} - {{ purchase.supplier_name }}
          </option>
        </select>
        <small v-if="isLoadingPurchases" class="text-muted">
          <i class="fa fa-spinner fa-spin me-1"></i>
          Chargement des commandes...
        </small>
      </div>

      <div class="col-md-3">
        <label class="form-label small fw-semibold">Référence retour</label>
        <input
          type="text"
          class="form-control form-control-sm"
          v-model="form.reference"
          placeholder="Auto-générée si vide"
          :disabled="submitting"
        />
      </div>

      <div class="col-md-4">
        <label class="form-label small fw-semibold">Date du retour *</label>
        <input type="date" class="form-control form-control-sm" v-model="form.return_date" :disabled="submitting" />
      </div>
    </div>

    <div v-if="purchaseDetail" class="return-context mb-3">
      <div>
        <span class="small text-muted d-block">Fournisseur</span>
        <strong>{{ purchaseDetail.supplier_name }}</strong>
      </div>
      <div>
        <span class="small text-muted d-block">Commande</span>
        <strong>{{ purchaseDetail.reference }}</strong>
      </div>
      <div>
        <span class="small text-muted d-block">Total commande</span>
        <strong>{{ $format.currency(purchaseDetail.total_amount || 0) }}</strong>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Commentaire</label>
      <input
        type="text"
        class="form-control form-control-sm"
        v-model="form.comment"
        placeholder="Motif du retour, référence avoir, état des produits..."
        :disabled="submitting"
      />
    </div>

    <div v-if="errorMessage" class="alert alert-danger py-2">
      {{ errorMessage }}
    </div>

    <div class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mb-0">Produits à retourner</h6>
      <small class="text-muted" v-if="returnLines.length">
        Total retour : <strong>{{ $format.currency(form.total_amount) }}</strong>
      </small>
    </div>

    <BootstrapDataTable
      :rows="returnLines"
      :columns="returnLineColumns"
      row-key="purchase_item_id"
      :loading="isLoadingPurchaseDetail"
      :client-pagination="false"
      :show-refresh="false"
      :show-export="false"
      :show-print="false"
      :show-footer="false"
      empty-text="Sélectionnez une commande validée avec des quantités encore retournables."
      class="mb-3"
    >
      <template #cell-product_name="{ row }">
        <div class="d-flex flex-column">
          <span class="fw-semibold">{{ row.product_name }}</span>
          <small class="text-muted">{{ row.product_sku || '-' }}</small>
        </div>
      </template>

      <template #cell-ordered_quantity="{ row }">
        <div class="text-end">{{ row.ordered_quantity }}</div>
      </template>

      <template #cell-lot_number="{ row }">
        {{ row.lot_number || '-' }}
      </template>

      <template #cell-expiry_date="{ row }">
        {{ row.expiry_date ? $format.date(row.expiry_date) : '-' }}
      </template>

      <template #cell-returned_quantity="{ row }">
        <div class="text-end">{{ row.returned_quantity }}</div>
      </template>

      <template #cell-returnable_quantity="{ row }">
        <div class="text-end fw-semibold">{{ row.returnable_quantity }}</div>
      </template>

      <template #cell-quantity="{ row }">
        <input
          type="number"
          min="0"
          :max="row.returnable_quantity"
          class="form-control form-control-sm text-end"
          v-model.number="row.quantity"
          :disabled="submitting"
          @input="recalculateTotals"
        />
      </template>

      <template #cell-unit_price="{ row }">
        <div class="text-end">{{ $format.currency(row.unit_price || 0) }}</div>
      </template>

      <template #cell-line_total="{ row }">
        <div class="text-end fw-semibold">
          {{ $format.currency((row.quantity || 0) * (row.unit_price || 0)) }}
        </div>
      </template>
    </BootstrapDataTable>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="submitting" @click="$emit('cancel')">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm btn-icon-label"
        :loading="submitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save"></i>
        <span class="btn-label ms-1">Enregistrer</span>
      </AppLoadingButton>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupplierReturnForm',
  props: {
    mode: { type: String, required: true },
    supplierReturn: { type: Object, default: () => ({}) },
    initialPurchaseId: { type: [Number, String], default: null },
    submitting: { type: Boolean, default: false },
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      form: {
        id: undefined,
        reference: null,
        purchase_id: null,
        return_date: '',
        status: 'draft',
        comment: '',
        total_amount: 0,
      },
      purchases: [],
      purchaseDetail: null,
      returnLines: [],
      returnLineColumns: [
        { key: 'product_name', label: 'Produit' },
        { key: 'ordered_quantity', label: 'Commandé', class: 'text-end', style: 'width: 110px;' },
        { key: 'lot_number', label: 'Lot', style: 'width: 140px;' },
        { key: 'expiry_date', label: 'Expiration', style: 'width: 140px;' },
        { key: 'returned_quantity', label: 'Déjà retourné', class: 'text-end', style: 'width: 130px;' },
        { key: 'returnable_quantity', label: 'Max', class: 'text-end', style: 'width: 90px;' },
        { key: 'quantity', label: 'Qté retour *', style: 'width: 140px;' },
        { key: 'unit_price', label: 'PU', class: 'text-end', style: 'width: 130px;' },
        { key: 'line_total', label: 'Total', class: 'text-end', style: 'width: 140px;' },
      ],
      isLoadingPurchases: false,
      isLoadingPurchaseDetail: false,
      errorMessage: '',
    };
  },
  async mounted() {
    this.initForm();
    await this.fetchPurchases();

    if (this.form.purchase_id) {
      await this.loadPurchaseReturnables();
    }
  },
  methods: {
    initForm() {
      if (this.mode === 'edit' && this.supplierReturn?.id) {
        this.form.id = this.supplierReturn.id;
        this.form.reference = this.supplierReturn.reference || null;
        this.form.purchase_id = this.supplierReturn.purchase_id || null;
        this.form.return_date = (this.supplierReturn.return_date || '').slice(0, 10);
        this.form.status = this.supplierReturn.status || 'draft';
        this.form.comment = this.supplierReturn.comment || '';
        this.form.total_amount = Number(this.supplierReturn.total_amount || 0);
        return;
      }

      this.form.return_date = new Date().toISOString().slice(0, 10);
      this.form.purchase_id = this.initialPurchaseId ? Number(this.initialPurchaseId) : null;
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    async fetchPurchases() {
      this.isLoadingPurchases = true;
      try {
        const res = await this.$api.get('/purchases', {
          params: {
            status: 'validated',
            limit: 500,
            sort_by: 'purchase_date',
            sort_dir: 'desc',
          },
        });
        this.purchases = res.data.data || res.data || [];
      } catch (error) {
        this.errorMessage = 'Impossible de charger les commandes validées.';
        this.purchases = [];
      } finally {
        this.isLoadingPurchases = false;
      }
    },

    async loadPurchaseReturnables() {
      this.purchaseDetail = null;
      this.returnLines = [];
      this.errorMessage = '';

      if (!this.form.purchase_id) {
        this.recalculateTotals();
        return;
      }

      this.isLoadingPurchaseDetail = true;
      try {
        const res = await this.$api.get(`/supplier-returns/purchases/${this.form.purchase_id}/items`);
        this.purchaseDetail = res.data.data;
        const existingByPurchaseItem = this.existingQuantitiesByPurchaseItem();

        this.returnLines = (this.purchaseDetail.items || [])
          .map((item) => {
            const existingQuantity = existingByPurchaseItem[item.id] || 0;
            return {
              purchase_item_id: item.id,
              product_id: item.product_id,
              product_name: item.product_name,
              product_sku: item.product_sku,
              ordered_quantity: Number(item.quantity || 0),
              returned_quantity: Number(item.returned_quantity || 0),
              returnable_quantity: Number(item.returnable_quantity || 0),
              quantity: existingQuantity,
              unit_price: Number(item.unit_price || 0),
              lot_number: item.lot_number || '',
              expiry_date: item.expiry_date || null,
            };
          })
          .filter((item) => item.returnable_quantity > 0 || item.quantity > 0);

        this.recalculateTotals();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Impossible de charger les produits retournables de cette commande.';
      } finally {
        this.isLoadingPurchaseDetail = false;
      }
    },

    existingQuantitiesByPurchaseItem() {
      const quantities = {};
      (this.supplierReturn?.items || []).forEach((item) => {
        quantities[item.purchase_item_id] = Number(item.quantity || 0);
      });
      return quantities;
    },

    recalculateTotals() {
      let total = 0;
      this.returnLines.forEach((line) => {
        const quantity = Math.max(0, Number(line.quantity || 0));
        line.quantity = Math.min(quantity, Number(line.returnable_quantity || 0));
        total += line.quantity * Number(line.unit_price || 0);
      });
      this.form.total_amount = total;
    },

    submit() {
      if (this.submitting) return;

      this.errorMessage = '';
      this.recalculateTotals();

      if (!this.form.purchase_id) {
        this.errorMessage = 'Sélectionnez une commande fournisseur validée.';
        return;
      }

      if (!this.form.return_date) {
        this.errorMessage = 'Renseignez la date du retour.';
        return;
      }

      const items = this.returnLines
        .filter((line) => Number(line.quantity || 0) > 0)
        .map((line) => ({
          purchase_item_id: line.purchase_item_id,
          quantity: Number(line.quantity || 0),
        }));

      if (!items.length) {
        this.errorMessage = 'Renseignez au moins une quantité à retourner.';
        return;
      }

      this.$emit('save', {
        id: this.form.id,
        reference: this.form.reference || null,
        purchase_id: this.form.purchase_id,
        return_date: this.form.return_date,
        comment: this.form.comment,
        items,
      });
    },
  },
};
</script>

<style scoped>
.return-context {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(33, 37, 41, 0.08);
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 767.98px) {
  .return-context {
    grid-template-columns: 1fr;
  }
}
</style>
